import React, { useContext, useState, useEffect } from "react";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import SelectInput from "common/SelectInput";
import Request from "common/Request";
import Table from "common/graphs/Table/Table";
import { parseIP, durationToInt } from "common/utils";
import { NumberCell, TimeCell } from "common/graphs/Table/TableRenders";
import { ReportContextControlsLayout } from "../utils";
import { hoursScopeChoices, hoursAsPeriod } from "common/constants";
import View from "common/View";
import { openSubscriberDetails } from "views/Status/Subscribers/Report/index";
import help from "help/viewDoSSynStats.md";
import { dosSynRetrieval } from "../api";

const fixHeader = (response) => {
  const [head, ...rows] = response.trim().split('\n');
  return [
    head.replace(/\sFAIL-SYN$/, ' TOP-FAIL-SYN'),
    ...rows
  ].join('\n');
}

const retrieval = ({ hours, mode, bqnId }) =>
  dosSynRetrieval({ hours, mode, bqnId }).then(fixHeader);

const returnTo = "viewDoSSynStats";

const getColumns = ({ timePeriod }) => [
    {
      label: "TIME",
      id: "TIME",
      numeric: false,
    },
    {
      label: "IP-ADR",
      id: "IP-ADDRESS",
      numeric: false,
      parse: parseIP,
      cellClassName: "hyperlink-text",
      onClick: (addr) => openSubscriberDetails({ addr }, timePeriod, returnTo),
      parseFilter: (value) => value
    },
    {
      label: "SIDE",
      id: "SIDE",
      numeric: false,
    },
    {
      label: "DIRECTION",
      id: "DIRECTION",
      numeric: false,
    },
    {
      label: "FAIL-SYN",
      id: "FAIL-SYN",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "MAX-RATE",
      id: "MAX-RATE",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={false} precision={0} />
      ),
    },
    {
      label: "DURATION",
      id: "DURATION",
      numeric: true,
      renderCell: (value) =>
        (value === "ongoing") ? "ongoing" : <TimeCell value={value} />,
      parse: (value) => (value === "ongoing" ? null : durationToInt(value)),
    },
    {
      label: "TOP-CONTRIBUTOR",
      id: "TOP-CONTRIBUTOR",
      numeric: false,
      parse: parseIP,
      parseFilter: (value) => value
    },
    {
      label: "TOP-FAIL-SYN",
      id: "TOP-FAIL-SYN",
      numeric: true,
      renderCell: (value) => <NumberCell value={value} hasNA={true} />,
    },
  ];

const DoSSynStats = () => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const [hours, setHours] = useState(24);

  const doLoad = () => {
    const doingRetrieval = retrieval({ hours, bqnId, mode }).catch(
      (error) => error
    );
    setRequest(doingRetrieval);
  };
  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the command would not carry its option, and the response would be that of
  // the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doLoad();
    return actions.recv('do-load', doLoad);
  }, [hours, mode, bqnId]);


  return (
    <View title="DoS syn attacks over time" mdHelp={help}>
        <ReportContextControlsLayout>
          <ColumnsGrid columns={4} maxWidth="15rem" minWidth="9cm" rowGap="0">
            <SelectInput
              label="Most recent"
              name="hours"
              onChange={(event) => setHours(event.target.value)}
              defaultValue={hours}
              options={hoursScopeChoices}
            />
          </ColumnsGrid>
        </ReportContextControlsLayout>
        <div className="body flex-panel-column">
          {request === null ? null : (
            <Request during={request.then((response) => ({ response }))}>
              {({ response }) => {
                const [header, ...rows] = response.trim().split("\n");
                const headerAttrs = header.trim().split(/\s+/);
                const newRows = rows.map((row) => {
                  const rowAttrs = row.trim(/\s/).split(/\s+/);
                  const newEntry = rowAttrs.reduce((acc, attr, index) => {
                    acc[headerAttrs[index]] = attr;
                    return acc;
                  }, {});
                  return newEntry;
                });
                return (
                  <div className="body table-responsive">
                    <Table
                      isSelectable={false}
                      isRowSelectable={false}
                      data={newRows}
                      sort={true}
                      columns={getColumns({timePeriod: hoursAsPeriod[hours]})}
                      search={true}
                      csvPrefix="syn-attacks"
                      rowsPerPageNr={50}
                    />
                  </div>
                );
              }}
            </Request>
          )}
        </div>
    </View>
  );
};

export default DoSSynStats;
